import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { MessageSquareIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Sidebar } from './Sidebar'
import { MobileMoreMenu } from './MobileMoreMenu'
import { MobileBottomNav, MOBILE_BOTTOM_NAV_HEIGHT } from './MobileBottomNav'
import { DivisionProvider, DivisionFilter } from '../contexts/DivisionContext'
import { NotificationBell } from './NotificationBell'
import { ProfileMenu } from './ProfileMenu'
import { IncomingCallBanner } from './IncomingCallBanner'
import { useAppShellMode } from '../hooks/useAppShellMode'
import { usePermissions } from '../hooks/usePermissions'
import { useDesktopMessageNotifications } from '../hooks/useDesktopMessageNotifications'
import { useAuth } from '../contexts/AuthContext'

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation('common')
  const shell = useAppShellMode()
  const isMobileShell = shell === 'mobile'
  const { canAny } = usePermissions()
  const { user } = useAuth()
  const canMessages = canAny(['communication.chat', 'communication.inbox'])
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useDesktopMessageNotifications(Boolean(user))

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  const openMobileMenu = () => setMobileMenuOpen(true)
  const closeMobileMenu = () => setMobileMenuOpen(false)

  // Close drawer when switching to desktop browser shell
  useEffect(() => {
    if (!isMobileShell) setMobileMenuOpen(false)
  }, [isMobileShell])

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  return (
    <DivisionProvider>
      <div
        className="flex h-[100dvh] max-h-[100dvh] bg-gray-50 overflow-hidden"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {/* Desktop sidebar — hidden on phone widths and in installed PWA */}
        <div className="shell-desktop-flex flex-shrink-0">
          <Sidebar isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        </div>

        {/* Mobile / PWA More menu — light theme sheet (not dark desktop sidebar) */}
        {isMobileShell && (
          <MobileMoreMenu open={mobileMenuOpen} onClose={closeMobileMenu} />
        )}

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0">
          {/* Top bar for mobile / PWA — compact app chrome */}
          <header className="shell-mobile-block bg-white/95 backdrop-blur-md border-b border-gray-200/90 px-3 py-2.5 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center min-w-0">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-600/20">
                  <span className="text-white font-bold text-xs tracking-wide">RBC</span>
                </div>
                <div className="ml-2.5 min-w-0">
                  <span className="block text-[15px] font-semibold text-gray-900 leading-tight truncate">
                    {t('nav.fieldChrome')}
                  </span>
                  <span className="block text-[11px] text-gray-500 leading-tight truncate">
                    RBCFS
                  </span>
                </div>
              </div>
              {/* Overflow menu lives in bottom nav "More" — avoid dual drawers on mobile/PWA */}
              <div className="flex items-center gap-0.5">
                {canMessages && (
                  <Link
                    to="/messages"
                    className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Messages"
                    aria-label="Messages"
                  >
                    <MessageSquareIcon className="w-5 h-5 text-gray-600" />
                  </Link>
                )}
                <NotificationBell />
              </div>
            </div>
          </header>

          {/* Desktop header with division filter */}
          <div className="shell-desktop-block bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-semibold text-gray-900">RBC Field Management</h1>
                <DivisionFilter />
              </div>
              <div className="flex items-center space-x-4">
                <NotificationBell />
                <ProfileMenu />
              </div>
            </div>
          </div>

          {/* Page content — bottom pad for field tab bar + safe area on mobile/PWA */}
          <main
            className="shell-main-pad flex-1 overflow-y-auto overflow-x-hidden min-h-0 overscroll-y-contain"
            style={{
              paddingBottom: isMobileShell
                ? `calc(${MOBILE_BOTTOM_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px) + 0.5rem)`
                : undefined,
            }}
          >
            {children}
          </main>
        </div>

        {isMobileShell && <MobileBottomNav onOpenMenu={openMobileMenu} />}

        {/* Telebroad call screen-pop (realtime on call_events) */}
        <IncomingCallBanner />
      </div>
    </DivisionProvider>
  )
}
